import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { get, map } from "lodash";
import {
  Box,
  Card,
  Flex,
  FlexItem,
  H2,
  Icon,
  P,
  ButtonLink,
} from "orcs-design-system";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import urlPaths, { url } from "src/consts/urlPaths";
import integrationTypes from "../../integrationTypes";
import { JIRA_TEAMS_EXPORT_PHRASES } from "../../consts/jira";
import phrasesAreIncluded from "./phrasesAreIncluded";
import CardLogo from "./CardLogo";

const Datasources = ({ data, loading }) => {
  const workspace = useWorkspace();
  const workspaceFeatureFlags = get(workspace, "config.featureFlags", {});

  const { datasources } = data || {};

  // Generate URLs for create and open actions
  const createUrl = useMemo(
    () =>
      url(urlPaths.WORKSPACE_CONFIGURATION, {
        tabType: "datasources",
        actionType: "select",
      }),
    []
  );

  const getOpenUrl = (id) =>
    url(urlPaths.DATASOURCE, {
      dataSourceId: id,
    });

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      {!loading && (
        <FlexItem width="100%" height="100%" flex="1 1 auto" overflow="hidden">
          <Box height="100%" overflow="auto">
            <Flex flexDirection="column" gap="r">
              <Card key="create-new-ds">
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  flexWrap="wrap"
                >
                  <Flex alignItems="center">
                    <Icon icon={["far", "link"]} size="3x" color="greyLight" />
                    <FlexItem ml="r">
                      <H2 sizing="small">New integration</H2>
                      <P color="greyDark">Connect to third party systems</P>
                    </FlexItem>
                  </Flex>
                  <ButtonLink href={createUrl} small variant="ghost">
                    Create
                  </ButtonLink>
                </Flex>
              </Card>
              {map(datasources, ({ id, name, description, type }) => {
                const { logoSrc, logoAlt, hidden, heading } =
                  get(integrationTypes, type) || {};

                if (
                  !workspaceFeatureFlags?.enableJiraTeamsExport &&
                  phrasesAreIncluded(heading, JIRA_TEAMS_EXPORT_PHRASES)
                ) {
                  return null;
                }

                if (
                  !workspaceFeatureFlags?.enableAgents &&
                  phrasesAreIncluded(heading, ["agents"])
                ) {
                  return null;
                }

                return hidden ? null : (
                  <Card key={id}>
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      flexWrap="nowrap"
                    >
                      <Flex alignItems="center">
                        {logoSrc && <CardLogo src={logoSrc} alt={logoAlt} />}
                        <FlexItem ml="r">
                          <H2 sizing="small">{name}</H2>
                          <P color="greyDark">{description}</P>
                        </FlexItem>
                      </Flex>
                      <FlexItem flex="0 0 auto">
                        <ButtonLink href={getOpenUrl(id)} small variant="ghost">
                          Open
                        </ButtonLink>
                      </FlexItem>
                    </Flex>
                  </Card>
                );
              })}
            </Flex>
          </Box>
        </FlexItem>
      )}
    </Flex>
  );
};

Datasources.propTypes = {
  data: PropTypes.shape({
    datasources: PropTypes.arrayOf(PropTypes.object),
  }),
  loading: PropTypes.bool,
};

export default Datasources;
